import React from "react";
import { X } from "lucide-react";
import { FlowpiDialogSurface } from "../ui/flowpi-dialog";
import { FormError } from "../ui/feedback";
import { Button } from "../ui/button";
import { SERVICE_BRANCH_LABELS } from "../../lib/serviceConstants";
import CannibalStockSourceSnapshot from "./CannibalStockSourceSnapshot";

export const CANNIBAL_TRANSFER_DEFAULT_COMPANY = "PT Dari Visi";
export const CANNIBAL_TRANSFER_ROOT_CAUSE = "Device dikanibal.";

export function buildCannibalTransferProblem(removedParts = []) {
  const lines = removedParts.map(
    (part) => `• ${part.part_number || "-"} - ${part.part_name || "-"}`,
  );
  return `List PART yang dikanibal :\n${lines.join("\n")}`;
}

export function buildCannibalTransferPayload({ prefill, companyName, now = new Date() }) {
  return {
    category: "stock",
    branch: prefill.branch,
    date_in: now.toISOString(),
    owner: { name: companyName.trim(), pic: "", contact: "0", address: "" },
    device: {
      serial_number: prefill.serial_number,
      purchase_date: prefill.purchase_date,
      model_selection: prefill.model_selection,
    },
    unknown_stock_reference_confirmed: prefill.stock_reference_status === "unknown",
    warranty: { dvt: { duration_years: 1 }, imin: { status: "unknown", expiry_date: null } },
    accessories: { items: [], other_details: "" },
    notes: "",
    cannibal_transfer: {
      cannibal_device_id: prefill.cannibal_device_id,
      expected_updated_at: prefill.expected_updated_at,
    },
  };
}

function ReadOnlyField({ id, label, value, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold text-zinc-700" htmlFor={id}>{label}</label>
      <input
        id={id}
        value={value || "-"}
        readOnly
        className="field-readonly mt-2 w-full rounded-sm border px-3 py-2.5 text-sm outline-none"
      />
    </div>
  );
}

export default function CannibalTransferDialog({
  prefill,
  companyName,
  onCompanyNameChange,
  onCancel,
  onTransfer,
  submitting = false,
  error = "",
}) {
  if (!prefill) return null;

  const problem = prefill.problem || buildCannibalTransferProblem(prefill.removed_parts);
  const stockReference = prefill.stock_reference_status === "known"
    ? "Known"
    : prefill.stock_reference_status === "unknown"
      ? "Unknown"
      : prefill.stock_reference_status || "-";

  return (
    <FlowpiDialogSurface open onClose={onCancel} busy={submitting} size="workflow" accessibleTitle="Transfer to Service Stock" testId="cannibal-transfer-modal">
      <div className="flex max-h-[calc(100vh-48px)] w-[920px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-sm border border-border bg-background shadow-2xl">
        <div className="flex items-start justify-between border-b border-border px-7 py-5">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary">Cannibal Transfer</div>
            <h2 id="cannibal-transfer-title" className="mt-1 text-2xl font-semibold">Transfer to Service Stock</h2>
          </div>
          <Button type="button" onClick={onCancel} variant="ghost" size="icon" aria-label="Close Cannibal transfer">
            <X className="h-4 w-4" strokeWidth={2} />
          </Button>
        </div>

        <div className="min-h-0 overflow-y-auto px-7 py-6">
          <div className="grid gap-5 md:grid-cols-2">
            <ReadOnlyField id="cannibal-transfer-category" label="Category" value="Stock" />
            <ReadOnlyField id="cannibal-transfer-branch" label="Branch" value={SERVICE_BRANCH_LABELS[prefill.branch] || prefill.branch} />
          </div>

          <div className="mt-5 grid gap-5 md:grid-cols-3">
            <ReadOnlyField id="cannibal-transfer-serial" label="Serial Number" value={prefill.serial_number} />
            <ReadOnlyField id="cannibal-transfer-model" label="Model" value={prefill.model} />
            <ReadOnlyField id="cannibal-transfer-stock-reference" label="Stock Reference" value={stockReference} />
          </div>

          <div className="mt-5">
            <label className="text-xs font-semibold text-zinc-700" htmlFor="cannibal-transfer-company">Company Name</label>
            <input
              id="cannibal-transfer-company"
              value={companyName}
              onChange={(event) => onCompanyNameChange(event.target.value)}
              className="field-editable mt-2 w-full rounded-sm border px-3 py-2.5 text-sm outline-none transition-colors"
              autoFocus
            />
          </div>

          <div className="mt-5">
            <CannibalStockSourceSnapshot
              cannibalId={prefill.cannibal_id}
              removedParts={prefill.removed_parts || []}
              badge="Transfer preview"
              testId="cannibal-transfer-stock-source"
            />
          </div>

          <div className="mt-5">
            <label className="text-xs font-semibold text-zinc-700" htmlFor="cannibal-transfer-problem">Problem</label>
            <textarea
              id="cannibal-transfer-problem"
              value={problem || "-"}
              readOnly
              rows={Math.max(4, (prefill.removed_parts || []).length + 2)}
              className="field-readonly mt-2 w-full resize-none rounded-sm border px-3 py-2.5 text-sm leading-6 outline-none"
            />
          </div>

          <ReadOnlyField
            id="cannibal-transfer-root-cause"
            label="Root Cause"
            value={prefill.root_cause || CANNIBAL_TRANSFER_ROOT_CAUSE}
            className="mt-5"
          />

          <FormError className="mt-5">{error}</FormError>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border bg-muted px-7 py-4">
          <Button type="button" onClick={onCancel} disabled={submitting} variant="cancel" data-action-category="cancel">Cancel</Button>
          <Button type="button" onClick={onTransfer} disabled={!companyName.trim()} loading={submitting} loadingLabel="Transferring..." variant="success" data-action-category="submit-confirm">
            Transfer to Service Stock
          </Button>
        </div>
      </div>
    </FlowpiDialogSurface>
  );
}
